/**
 * @file CalendarMeetingsPanel.tsx
 * @brief "From your calendar" under Upcoming on Boardrooms, desktop app only:
 *        today's and tomorrow's Mac-calendar meetings, each with Record.
 *
 * @details
 * The web twin of the phone's CalendarMeetingsBlock (DES 06-10-2026). Record
 * opens the setup page with the title and attendees filled in; nothing starts
 * on its own and nothing is sent until the user starts the meeting.
 *
 * States: hidden (browser / Windows / old app) → "Connect your calendar"
 * (✕ = not now, per user) → "Calendar access is off" (System Settings) →
 * "No meetings today or tomorrow" → the meetings, with Disconnect by the
 * heading. Same row look as the Upcoming list beside it.
 *
 * Phone parity (DES 14-10-2026): after ✕ the heading stays with "Connect"
 * (it used to vanish for good); the connected heading has a ⋯ menu with the
 * Mac's calendars (tick = shown, saved per user) and Disconnect; only
 * meeting-like events show (guests or a call link), and the empty state says
 * so. Desktop builds before v11 can't list calendars: the menu then holds
 * Disconnect alone.
 *
 * Related: ./useDesktopCalendar.ts, ./BoardroomSetupPage.tsx (`fromCalendar`)
 *
 * @author DES
 * @date 06-10-2026
 */

import { format, isToday, isTomorrow } from "date-fns";
import { useState, type ReactNode } from "react";
import { CalendarDays, CalendarOff, CalendarPlus, CircleX, Mic, MoreHorizontal, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { SearchableMenuList } from "@/components/ui/SearchableMenuList";
import { cn } from "@/shared/theme/cn";
import type { CalendarMeeting, CalendarMeetingPrefill } from "./calendarMeetings";
import { connectHint, type DesktopCalendarState } from "./useDesktopCalendar";

const MAX_ROWS = 4;

function isNow(m: CalendarMeeting, now = Date.now()) {
  return m.startMs <= now && now < m.endMs;
}

export function whenLabel(m: CalendarMeeting): string {
  const start = new Date(m.startMs);
  const end = new Date(m.endMs);
  if (isNow(m)) return `Now · until ${format(end, "HH:mm")}`;
  const day = isToday(start) ? "Today" : isTomorrow(start) ? "Tomorrow" : format(start, "EEE d MMM");
  return `${day} ${format(start, "HH:mm")}–${format(end, "HH:mm")}`;
}

/** Denied row (desktop v12+): Privacy & Security > Calendars. Shared with Home. */
export function OpenCalendarSettingsButton({ calendar }: { calendar: DesktopCalendarState }) {
  if (!calendar.canOpenSettings) return null;
  return (
    <button
      type="button"
      onClick={calendar.openSettings}
      className="shrink-0 rounded-full bg-brand px-3 py-1.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
    >
      Open System Settings
    </button>
  );
}

function Heading({ right }: { right?: ReactNode }) {
  return (
    <div className="mb-1 mt-5 flex items-center justify-between gap-3">
      <h2 className="text-lg font-semibold tracking-[-0.5px] text-app-text/40">From your calendar</h2>
      {right}
    </div>
  );
}

/** ⋯ on the connected heading: the Mac's calendars (tick = shown) + Disconnect. */
export function CalendarMenu({ calendar }: { calendar: DesktopCalendarState }) {
  const [open, setOpen] = useState(false);
  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) void calendar.loadCalendars();
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Calendar options"
          className="rounded-full p-1 text-app-text-secondary transition-colors hover:text-app-text"
        >
          <MoreHorizontal className="h-5 w-5" strokeWidth={1.75} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 p-1.5">
        {calendar.canChooseCalendars ? (
          <>
            <SearchableMenuList
              title="Show calendars"
              multiple
              searchPlaceholder="Search calendars"
              emptyText="Loading your calendars…"
              items={calendar.calendars.map((c) => ({
                value: c.id,
                label: c.title || "Calendar",
                group: c.sourceTitle || "Other",
                icon: (
                  <span
                    className="block h-3 w-3 rounded-full"
                    style={{ backgroundColor: c.color || "#8E8E93" }}
                  />
                ),
              }))}
              selected={calendar.calendars
                .filter((c) => !calendar.hiddenCalendarIds.has(c.id))
                .map((c) => c.id)}
              onSelect={(id) => calendar.setCalendarShown(id, calendar.hiddenCalendarIds.has(id))}
            />
            <div className="my-1.5 h-px bg-border" />
          </>
        ) : null}
        <DropdownMenuItem onSelect={calendar.disconnect} className="gap-2 text-destructive focus:text-destructive">
          <CircleX className="h-4 w-4" strokeWidth={1.75} />
          Disconnect
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function CalendarMeetingsPanel({
  calendar,
  meetings,
  onRecord,
}: {
  calendar: DesktopCalendarState;
  /** The calendar's meetings minus Verblizr's own scheduled ones. */
  meetings: CalendarMeeting[];
  onRecord: (prefill: CalendarMeetingPrefill) => void;
}) {
  const { enabled, status, loaded, connectDismissed } = calendar;
  if (!enabled || status === "loading" || status === "unsupported") return null;

  const notNow = (
    <button
      type="button"
      onClick={calendar.dismissConnect}
      aria-label="Not now"
      className="shrink-0 rounded-full p-1 text-app-text-secondary transition-colors hover:text-app-text"
    >
      <X className="h-[18px] w-[18px]" />
    </button>
  );

  // Folded after ✕ (phone parity, DES 14-10-2026): the heading stays with
  // Connect, which brings the row back (and asks macOS when never asked).
  if (connectDismissed && status !== "authorized") {
    return (
      <section>
        <Heading
          right={
            <button
              type="button"
              onClick={() => {
                calendar.undoDismiss();
                if (status === "notDetermined") void calendar.connect();
              }}
              className="text-sm font-medium text-app-text-secondary transition-colors hover:text-app-text"
            >
              Connect
            </button>
          }
        />
      </section>
    );
  }

  if (status === "notDetermined") {
    return (
      <section>
        <Heading />
        <div className="flex items-center gap-3 py-2">
          <button
            type="button"
            onClick={() => void calendar.connect()}
            disabled={calendar.connectPhase === "waiting"}
            aria-busy={calendar.connectPhase === "waiting"}
            className="flex min-w-0 flex-1 items-center gap-3 text-left transition-opacity hover:opacity-80 disabled:cursor-wait disabled:hover:opacity-100"
          >
            <CalendarPlus className="h-6 w-6 shrink-0 text-brand" strokeWidth={1.5} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[17px] font-bold text-app-text">Connect your calendar</span>
              <span className="block text-sm text-app-text-secondary" aria-live="polite">
                {connectHint(calendar, "See your meetings here and record them in one click")}
              </span>
            </span>
          </button>
          {notNow}
        </div>
      </section>
    );
  }

  if (status !== "authorized") {
    return (
      <section>
        <Heading />
        <div className="flex items-center gap-3 py-2">
          <CalendarOff className="h-6 w-6 shrink-0 text-app-text-secondary" strokeWidth={1.5} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[17px] font-bold text-app-text">Calendar access is off</span>
            <span className="block text-sm text-app-text-secondary">
              Turn it on in System Settings → Privacy &amp; Security → Calendars
            </span>
          </span>
          <OpenCalendarSettingsButton calendar={calendar} />
          {notNow}
        </div>
      </section>
    );
  }

  if (!loaded) return null;

  if (meetings.length === 0) {
    return (
      <section>
        <Heading right={<CalendarMenu calendar={calendar} />} />
        <div className="flex items-center gap-3 py-2">
          <CalendarDays className="h-6 w-6 shrink-0 text-app-text-secondary" strokeWidth={1.5} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[17px] font-bold text-app-text">No meetings today or tomorrow</span>
            <span className="block text-sm text-app-text-secondary">
              Only events with guests or a call link show here. Missing your work calendar? Add it in System
              Settings → Internet Accounts.
            </span>
          </span>
        </div>
      </section>
    );
  }

  const shown = meetings.slice(0, MAX_ROWS);
  const more = meetings.length - shown.length;
  return (
    <section>
      <Heading right={<CalendarMenu calendar={calendar} />} />
      {shown.map((m) => {
        const live = isNow(m);
        const n = m.attendees.length;
        return (
          <div key={m.id} className="flex items-center gap-3 py-2">
            <CalendarDays
              className={cn("h-6 w-6 shrink-0", live ? "text-brand" : "text-app-text-secondary")}
              strokeWidth={1.5}
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[17px] font-bold text-app-text">{m.title}</span>
              <span className={cn("block truncate text-sm", live ? "text-brand" : "text-app-text-secondary")}>
                {whenLabel(m)}
                {n > 0 ? `  ·  ${n} ${n === 1 ? "person" : "people"}` : ""}
              </span>
            </span>
            <button
              type="button"
              onClick={() => onRecord({ title: m.title, attendees: m.attendees })}
              aria-label={`Record ${m.title}`}
              className="inline-flex h-[34px] shrink-0 items-center gap-1.5 rounded-full bg-black/[0.04] px-3.5 text-sm font-semibold text-app-text transition-colors hover:bg-black/[0.08] dark:bg-white/[0.06] dark:hover:bg-white/[0.1]"
            >
              <Mic className="h-4 w-4 text-brand" strokeWidth={2} />
              Record
            </button>
          </div>
        );
      })}
      {more > 0 ? <p className="py-1 text-sm text-app-text-secondary">{more} more today and tomorrow</p> : null}
    </section>
  );
}
